// Natural Language Search (NLS) — a deterministic slot-extraction parser that
// runs BEFORE the normal search pipeline. NOT an AI system: no LLMs, embeddings,
// vector/semantic search, API calls, or external deps. Just static tables + a
// few bounded regexes → O(query length), no catastrophic backtracking, well
// under the 16ms/keystroke budget.
//
// Architecture:
//   query → normalize → extract slots → determine intent → build query → search
//
// Each parser extracts STRUCTURED meaning (QuerySlots) and never assembles the
// final query string itself. `buildQuery(slots)` is the single place where the
// rewritten query string is generated, so query understanding (slots) and query
// generation (string) stay decoupled — slots become the single source of truth
// reusable by future features (chips, previews, analytics, history, compare).

import { SearchItem } from "./types";
import { EntityLookup, matchEntity } from "./entityLookup";
import { parseDate, DateMatch } from "./dateParser";

const GEN_LOAD_BALANCE = "jvvnl/jvvnl-generation-load-balance";
const METRIC_PAGE = "jvvnl/jvvnl-metric";
const LOAD_MAP_PAGE = "jvvnl/load-map";
const FORECAST_PAGE = "jvvnl/forecast";

export type NaturalLanguageIntent =
  | "entity"
  | "weather"
  | "forecast"
  | "action"
  | "ranking"
  | "location";

/**
 * Structured slots extracted from a query. The single source of truth for query
 * understanding — every parser fills these, and `buildQuery` reads them. Future
 * features (chips, previews, analytics, history, AI-assist) should consume slots
 * rather than re-parse strings.
 */
export interface QuerySlots {
  entity?: string;
  weather?: string; // canonical weather token (temperature/humidity/...)
  forecast?: boolean;
  location?: string;
  entityType?: "feeder" | "substation" | "plant";
  voltageLevel?: string; // e.g. "132kv"
  action?: string; // action key, e.g. "forecast" / "metric" / "load-map"
  ranking?: "top" | "bottom";
  /** Ids of index entities matched for the `entity` slot (metadata for ranking boost). */
  entityIds?: string[];
  /** Recognised date context (single day or range). Metadata — never enters buildQuery. */
  date?: DateMatch;
}

export interface ParsedNaturalLanguageQuery {
  intent?: NaturalLanguageIntent;
  /** 0..1 — how confident the parse is. 0 = no pattern matched. */
  confidence: number;
  slots: QuerySlots;
  /** Query string handed to the existing searchIndex pipeline. */
  rewrittenQuery: string;
  /** Synthetic results to inject ABOVE normal results (action / compare). */
  injected?: SearchItem[];
}

// A parser returns the structured parts; the pipeline attaches rewrittenQuery
// via buildQuery and (for action/compare) any injected results.
interface ParseResult {
  intent: NaturalLanguageIntent;
  confidence: number;
  slots: QuerySlots;
  injected?: SearchItem[];
}

// ── Static tables ─────────────────────────────────────────────────────────

// Weather words → canonical token recognised by the existing weather-pairing
// system (WEATHER_TOKEN_TO_PARAM in deepLink.ts).
const WEATHER_ALIAS: Record<string, string> = {
  temp: "temperature",
  temperature: "temperature",
  humid: "humidity",
  humidity: "humidity",
  rainfall: "rain",
  precipitation: "rain",
  rain: "rain",
  sunlight: "sunshine",
  sunshine: "sunshine",
  solar: "radiation",
  ghi: "radiation",
  dni: "radiation",
  radiation: "radiation",
  wind: "wind",
  cloud: "cloud",
};

// Multi-word weather aliases (folded to single tokens during normalization).
const WEATHER_PHRASE_ALIAS: [string, string][] = [
  ["wind speed", "wind"],
  ["cloud cover", "cloud"],
];

const WEATHER_WORDS = new Set(Object.keys(WEATHER_ALIAS));

// Forecast trigger tokens / phrases (folded to the single token "forecast").
const FORECAST_PHRASES = [
  "future load",
  "future demand",
  "predicted load",
  "predicted demand",
  "expected load",
  "expected demand",
];
const FORECAST_WORDS = new Set([
  "forecast",
  "forecasts",
  "forecasted",
  "forcast",
  "prediction",
  "predicted",
  "projected",
  "expected",
]);

// Leading/standalone noise words removed before parsing.
const NOISE_WORDS = new Set([
  "show",
  "me",
  "open",
  "go",
  "to",
  "display",
  "view",
  "find",
  "get",
  "please",
]);
// "go to" → handled by dropping both "go" and "to"; keep "to" only as noise when
// it appears as a connector after "go" (we drop all standalone "to"s — harmless
// since entities don't contain a bare "to" token in this domain).

const LOCATION_PREP = new Set(["in", "near", "around"]);

const LOCATION_ENTITY: Record<string, "feeder" | "substation" | "plant"> = {
  feeders: "feeder",
  feeder: "feeder",
  substations: "substation",
  substation: "substation",
  stations: "substation",
  station: "substation",
  plants: "plant",
  plant: "plant",
};

const VOLTAGE_LEVELS = new Set(["11", "33", "132", "220", "400"]);

// ── Normalization ───────────────────────────────────────────────────────────

/**
 * Lowercase, collapse whitespace, fold multi-word weather/forecast phrases into
 * single canonical tokens, and strip leading/standalone noise words ("show me",
 * "go to", "open", …). Voltage like "132 kv" is folded to "132kv".
 */
export function normalizeNaturalLanguageQuery(query: string): string {
  let s = query.trim().toLowerCase().replace(/\./g, "").replace(/\s+/g, " ");
  if (!s) return "";

  // Fold "<n> kv" → "<n>kv".
  s = s.replace(/\b(\d{2,3})\s*kv\b/g, "$1kv");

  // Fold multi-word weather phrases → single token.
  for (const [phrase, canon] of WEATHER_PHRASE_ALIAS) {
    s = s.split(phrase).join(canon);
  }
  // Fold multi-word forecast phrases → "forecast".
  for (const phrase of FORECAST_PHRASES) {
    s = s.split(phrase).join("forecast");
  }

  // Strip noise tokens.
  const toks = s.split(" ").filter((t) => t && !NOISE_WORDS.has(t));
  return toks.join(" ");
}

// ── Slot helpers ──────────────────────────────────────────────────────────

function canonWeather(token: string): string | null {
  return WEATHER_ALIAS[token] ?? null;
}

/**
 * Split a token list into weather/forecast modifiers + the entity remainder.
 * Order-independent: handles "badawas temperature forecast", "forecast
 * temperature badawas", "badawas rain forecast", etc.
 */
function extractModifiers(tokens: string[]): {
  weather: string | null;
  forecast: boolean;
  rest: string[];
} {
  let weather: string | null = null;
  let forecast = false;
  const rest: string[] = [];
  for (const t of tokens) {
    const w = canonWeather(t);
    if (w && !weather) {
      weather = w;
      continue;
    }
    if (FORECAST_WORDS.has(t)) {
      forecast = true;
      continue;
    }
    rest.push(t);
  }
  return { weather, forecast, rest };
}

// ── Parsers (each returns ParseResult | null; no query strings) ───────────────

interface ActionDef {
  key: string;
  label: string;
  page: string;
  directQuery?: string;
  phrases: string[];
}

const ACTIONS: ActionDef[] = [
  {
    key: "forecast",
    label: "Open Forecast",
    page: FORECAST_PAGE,
    phrases: ["forecast", "forecast page"],
  },
  {
    key: "metric",
    label: "Open Metric",
    page: METRIC_PAGE,
    phrases: ["metric", "metrics", "metric page", "metrics page"],
  },
  {
    key: "load-map",
    label: "Open Load Map",
    page: LOAD_MAP_PAGE,
    phrases: ["load map", "load map page"],
  },
  {
    key: "weather",
    label: "Open Weather",
    page: GEN_LOAD_BALANCE,
    phrases: ["weather", "weather page"],
  },
  {
    key: "demand",
    label: "Open Demand",
    page: GEN_LOAD_BALANCE,
    phrases: ["demand", "demand page"],
  },
  {
    key: "generation",
    label: "Open Generation",
    page: GEN_LOAD_BALANCE,
    phrases: ["generation", "generation page"],
  },
];

/** Match score so a freshly-matched action sorts before exact > partial. */
function actionItem(a: ActionDef, score: number): SearchItem {
  return {
    id: `action-${a.key}`,
    type: "action",
    label: a.label,
    searchText: a.label.toLowerCase(),
    page: a.page,
    directQuery: a.directQuery,
    category: "Action",
    priority: score, // ranks above normal pages; exact > partial
    hint: "Go to",
    pageLabel: a.label.replace(/^Open /, ""),
  };
}

// Verbs that signal "Action Mode": even a bare verb ("open", "go to") should
// immediately surface all available actions, command-palette style.
const ACTION_VERB_RE = /^(open|go to|go|show|view|display)\b/;

// Action match score tiers — well above page/entity/metric so actions lead.
const ACTION_MATCH_SCORE = 2000;
const ACTION_EXACT_BONUS = 100;

/**
 * Does `query` match this action by exact / prefix / substring against any of
 * its phrases? Both sides are already normalized (lowercase, trimmed).
 * Returns the match score (0 = no match) so callers can rank exact above partial.
 */
function matchActionCommand(query: string, action: ActionDef): number {
  if (!query) return ACTION_MATCH_SCORE; // bare action-mode → surface all
  let best = 0;
  for (const phrase of action.phrases) {
    if (phrase === query) return ACTION_MATCH_SCORE + ACTION_EXACT_BONUS;
    if (phrase.startsWith(query)) best = Math.max(best, ACTION_MATCH_SCORE + 50);
    // Substring only for ≥3 chars, to avoid a single letter flooding actions.
    else if (query.length >= 3 && phrase.includes(query))
      best = Math.max(best, ACTION_MATCH_SCORE);
  }
  return best;
}

// 1) Action — discoverable on PARTIAL input, command-palette style. Engages when
//    either (a) the raw query starts with an action verb ("open", "go to",
//    "show", …) — Action Mode — or (b) the noise-stripped query partially matches
//    an action phrase. Matching actions are injected (all of them, exact first),
//    so "open" lists every action and "open fo" narrows to Open Forecast.
function parseAction(q: string, actionMode: boolean): ParseResult | null {
  // `q` is the noise-stripped query (verbs like "open"/"go to" already removed).
  const matches = ACTIONS.map((a) => ({ a, score: matchActionCommand(q, a) }))
    .filter((m) => m.score > 0)
    .sort((x, y) => y.score - x.score);

  // Outside Action Mode, only engage when there's a real partial match AND the
  // query isn't empty (a bare verb with no remainder only lists in Action Mode).
  if (!actionMode && (q.length === 0 || matches.length === 0)) return null;
  if (matches.length === 0) {
    if (!actionMode) return null;
    // Action Mode with an unmatched remainder (e.g. "open xyz") → list all,
    // letting the user see what commands exist.
    return {
      intent: "action",
      confidence: 0.5,
      slots: {},
      injected: ACTIONS.map((a) => actionItem(a, ACTION_MATCH_SCORE)),
    };
  }

  const top = matches[0];
  const exact = top.score >= ACTION_MATCH_SCORE + ACTION_EXACT_BONUS;
  return {
    intent: "action",
    confidence: exact ? 0.95 : actionMode ? 0.85 : 0.7,
    slots: { action: top.a.key },
    injected: matches.map((m) => actionItem(m.a, m.score)),
  };
}

/**
 * Ghost-text completion for action commands. Given the raw query, returns the
 * remaining characters of the best-matching action phrase when the (noise-
 * stripped) query is a non-empty prefix of exactly the leading phrase — so
 * "open fo" → "recast" (rendered as "open fo[recast]"). Empty when ambiguous or
 * no prefix match. Reuses the existing Tab-completion infrastructure.
 */
export function actionCompletion(query: string): string {
  const raw = query.trim().toLowerCase().replace(/\s+/g, " ");
  if (!raw) return "";
  const rest = normalizeNaturalLanguageQuery(query);
  if (!rest) return ""; // bare verb → no single completion
  const matches = ACTIONS.flatMap((a) =>
    a.phrases.filter((p) => p !== rest && p.startsWith(rest)),
  ).sort((x, y) => x.length - y.length);
  if (matches.length === 0) return "";
  // Only offer when it's unambiguous enough (the shortest completion).
  return matches[0].slice(rest.length);
}

// 2) Location — "<entityType> in/near/around <location>".
function parseLocation(q: string): ParseResult | null {
  const m = q.match(
    /^(feeders?|substations?|stations?|plants?)\s+(in|near|around)\s+(.+)$/,
  );
  if (!m) return null;
  const entityType = LOCATION_ENTITY[m[1]];
  const location = m[3].trim();
  if (!entityType || !location || !LOCATION_PREP.has(m[2])) return null;
  return {
    intent: "location",
    confidence: 0.9,
    slots: { location, entityType },
  };
}

// 3) Ranking — "top/bottom [N] feeders", "highest/lowest/best/worst … feeders".
const RANKING_TOP = /\b(top|highest|best|peak)\b/;
const RANKING_BOTTOM = /\b(bottom|lowest|worst)\b/;
function parseRanking(q: string): ParseResult | null {
  // Must be about feeders/consumers/load/demand to count as a ranking query.
  if (!/\b(feeders?|consumers?|load|demand|generators?)\b/.test(q)) return null;
  if (RANKING_BOTTOM.test(q)) {
    return { intent: "ranking", confidence: 0.9, slots: { ranking: "bottom" } };
  }
  if (RANKING_TOP.test(q)) {
    return { intent: "ranking", confidence: 0.9, slots: { ranking: "top" } };
  }
  return null;
}

// True when a known entity name spans one of the given modifier tokens — e.g.
// the plant "Akshvi Solar Line" contains "solar", a weather word. In that case
// the word is part of the name, NOT a weather/forecast modifier, so those
// parsers must defer to the bare-entity parser. Uses the longest-span matcher
// so it works even when extra tokens precede/follow the entity name.
function modifierIsInsideEntity(
  tokens: string[],
  isModifier: (t: string) => boolean,
  lookup?: EntityLookup,
): boolean {
  if (!lookup) return false;
  const hit = matchEntity(tokens, lookup);
  if (!hit) return false;
  const [start, end] = hit.span;
  if (end - start < 2) return false; // single-token span can't "contain" a word
  for (let i = start; i < end; i++) {
    if (isModifier(tokens[i])) return true;
  }
  return false;
}

// 4) Weather — an entity plus a weather word (optionally a forecast word too),
//    in any order, with optional "at/for/of" connectors.
function parseWeather(q: string, lookup?: EntityLookup): ParseResult | null {
  const tokens = stripConnectors(q.split(" ").filter(Boolean));
  if (!tokens.some((t) => WEATHER_WORDS.has(t))) return null;
  if (modifierIsInsideEntity(tokens, (t) => WEATHER_WORDS.has(t), lookup))
    return null; // weather word is part of an entity name
  const { weather, forecast, rest } = extractModifiers(tokens);
  if (!weather) return null;
  const resolved = resolveEntity(rest.join(" "), lookup);
  if (!resolved) return null; // bare weather → let normal search handle it
  // Higher confidence when a connector ("at"/"for") signalled the pairing.
  const conf = /\b(at|for|of)\b/.test(q) ? 0.95 : forecast ? 0.9 : 0.9;
  return {
    intent: forecast ? "forecast" : "weather",
    confidence: conf,
    slots: {
      entity: resolved.entity,
      weather,
      ...(resolved.entityIds ? { entityIds: resolved.entityIds } : {}),
      ...(forecast ? { forecast: true } : {}),
    },
  };
}

// 5) Forecast — an entity plus a forecast word, in any order.
function parseForecast(q: string, lookup?: EntityLookup): ParseResult | null {
  const tokens = stripConnectors(q.split(" ").filter(Boolean));
  if (!tokens.some((t) => FORECAST_WORDS.has(t))) return null;
  if (modifierIsInsideEntity(tokens, (t) => FORECAST_WORDS.has(t), lookup))
    return null; // forecast word is part of an entity name
  const { weather, forecast, rest } = extractModifiers(tokens);
  if (!forecast) return null;
  const resolved = resolveEntity(rest.join(" "), lookup);
  if (!resolved) return null; // bare forecast → normal search
  const conf = /\b(for|of)\b/.test(q) ? 0.95 : 0.9;
  return {
    intent: "forecast",
    confidence: conf,
    slots: {
      entity: resolved.entity,
      forecast: true,
      ...(resolved.entityIds ? { entityIds: resolved.entityIds } : {}),
      ...(weather ? { weather } : {}),
    },
  };
}

// 6) Voltage — a voltage level (optionally with an entity-type word).
function parseVoltage(q: string, lookup?: EntityLookup): ParseResult | null {
  const m = q.match(/\b(\d{2,3})kv\b/);
  if (!m || !VOLTAGE_LEVELS.has(m[1])) return null;
  const voltageLevel = `${m[1]}kv`;
  // Keep any entity-type word so "132kv substations" still searches usefully.
  const rest = q
    .split(" ")
    .filter((t) => t && t !== voltageLevel)
    .join(" ")
    .trim();
  const resolved = rest ? resolveEntity(rest, lookup) : null;
  return {
    intent: "entity",
    confidence: 0.7,
    slots: {
      voltageLevel,
      ...(resolved ? { entity: resolved.entity } : {}),
      ...(resolved?.entityIds ? { entityIds: resolved.entityIds } : {}),
    },
  };
}

// 7) Entity (fallback) — a bare entity name with no other modifier. Only fires
//    when a lookup is available AND the query resolves to a known index entity,
//    so "badawas" is understood as an entity slot. Without a lookup it returns
//    null (the query falls through to normal search unchanged — today's behavior).
function parseEntity(q: string, lookup?: EntityLookup): ParseResult | null {
  if (!lookup) return null;
  const tokens = stripConnectors(q.split(" ").filter(Boolean));
  const hit = matchEntity(tokens, lookup);
  if (!hit) return null;
  return {
    intent: "entity",
    confidence: 0.85,
    slots: { entity: hit.name, entityIds: hit.ids },
  };
}

// Drop "at/for/of" connectors used between a weather word and an entity.
function stripConnectors(tokens: string[]): string[] {
  return tokens.filter((t) => t !== "at" && t !== "for" && t !== "of");
}

// Resolve the entity remainder against the search index. When a lookup is
// available and the remainder contains a known entity name, returns the
// canonical name + matched ids (longest span wins). Falls back to the raw
// remainder (today's behavior) when there's no lookup or no index hit.
function resolveEntity(
  rest: string,
  lookup?: EntityLookup,
): { entity: string; entityIds?: string[] } | null {
  const raw = rest.trim();
  if (!raw) return null;
  if (lookup) {
    const hit = matchEntity(raw.split(" ").filter(Boolean), lookup);
    if (hit) return { entity: hit.name, entityIds: hit.ids };
  }
  return { entity: raw };
}

// ── Query builder — the ONLY place final query strings are generated ─────────

const RANKING_QUERY: Record<"top" | "bottom", string> = {
  top: "top feeders",
  bottom: "bottom feeders",
};

/**
 * Generate the final search-engine query string from extracted slots. Keeping
 * this as the single generation point means parsers only ever produce structured
 * data; adding a slot never spreads string assembly across the codebase.
 */
export function buildQuery(slots: QuerySlots, original: string): string {
  if (slots.ranking) return RANKING_QUERY[slots.ranking];

  if (slots.location) return slots.location; // entity-type filter applied later

  if (slots.entity || slots.weather || slots.forecast || slots.voltageLevel) {
    const parts: string[] = [];
    if (slots.voltageLevel) parts.push(slots.voltageLevel);
    if (slots.entity) parts.push(slots.entity);
    if (slots.weather) parts.push(slots.weather);
    if (slots.forecast) parts.push("forecast");
    const built = parts.join(" ").trim();
    if (built) return built;
  }

  // Action / compare carry their result via `injected`; the query text is
  // irrelevant for navigation, so pass the original through unchanged.
  return original;
}

// ── Entry point ───────────────────────────────────────────────────────────

// Non-action parsers, tried in priority order; first match wins. Action is
// handled first and separately (it needs the raw query to detect Action Mode).
// `parseEntity` is LAST: it's the bare-entity fallback, so explicit-intent
// parsers (location/ranking/weather/forecast/voltage) win when present.
const PARSERS: ((q: string, lookup?: EntityLookup) => ParseResult | null)[] = [
  parseLocation,
  parseRanking,
  parseWeather,
  parseForecast,
  parseVoltage,
  parseEntity,
];

function pack(query: string, r: ParseResult): ParsedNaturalLanguageQuery {
  return {
    intent: r.intent,
    confidence: r.confidence,
    slots: r.slots,
    rewrittenQuery: buildQuery(r.slots, query),
    injected: r.injected,
  };
}

export function rewriteNaturalLanguageQuery(
  query: string,
  lookup?: EntityLookup,
  now: Date = new Date(),
): ParsedNaturalLanguageQuery {
  const raw = query.trim().toLowerCase().replace(/\s+/g, " ");
  // Action Mode: raw query begins with an action verb ("open", "go to", …).
  const actionMode = ACTION_VERB_RE.test(raw);
  const normalized = normalizeNaturalLanguageQuery(query);

  // ── Date pre-pass: extract + remove date tokens BEFORE the parsers run, so
  //    every existing parser sees a date-free query and behaves as before. The
  //    DateMatch is attached to the resulting slots below.
  let dateMatch: DateMatch | null = null;
  let parseQuery = normalized;
  if (normalized) {
    const toks = normalized.split(" ").filter(Boolean);
    dateMatch = parseDate(toks, now);
    if (dateMatch) {
      const drop = new Set(dateMatch.matchedTokens);
      parseQuery = toks.filter((_, i) => !drop.has(i)).join(" ").trim();
    }
  }

  const attachDate = (
    p: ParsedNaturalLanguageQuery,
  ): ParsedNaturalLanguageQuery =>
    dateMatch ? { ...p, slots: { ...p.slots, date: dateMatch } } : p;

  // Action runs FIRST (highest priority) and on PARTIAL input. Note: action
  // detection uses the date-free query so "forecast tomorrow" still matches the
  // forecast action while carrying the date.
  const action = parseAction(parseQuery, actionMode);
  if (action) return attachDate(pack(query, action));

  if (!parseQuery) {
    // Bare date (e.g. "tomorrow"): no other intent. Pass empty query to normal
    // search but still surface the date slot + pill. With no date, preserve the
    // original raw-query fallthrough (don't substitute the noise-stripped form).
    return attachDate({ rewrittenQuery: dateMatch ? "" : query, confidence: 0, slots: {} });
  }

  for (const parser of PARSERS) {
    const r = parser(parseQuery, lookup);
    if (r) return attachDate(pack(query, r));
  }

  // No pattern matched: when a date was stripped, hand the date-free remainder
  // to search (so date tokens don't pollute ranking); otherwise preserve the
  // original raw query exactly as before this pass.
  return attachDate({ rewrittenQuery: dateMatch ? parseQuery : query, confidence: 0, slots: {} });
}
